/**
 * SignableRequirement Entity Schemas
 *
 * The binding/policy half of the signed-agreement architecture: a polymorphic
 * host (a TicketedEvent today) requires a document (a Form, by publicId soft-FK)
 * signed, with a completion policy and the set of signer roles that must EACH
 * sign for the requirement to be satisfied for a subject. Underlying Prisma
 * model: `SignableRequirement` (apps/backend). Generalizes the legacy
 * `TicketedEventWaiverRequirement`.
 *
 * `completionTiming`:
 *  - `at_registration` (default) blocks order confirmation / application approval.
 *  - `before_attendance` does not block; recorded as outstanding for door staff.
 *
 * `requiredSignerRoles` is a non-empty set; completion = every listed role has a
 * `complete` agreement for the subject. `requiresSignature` gates whether those
 * agreements must carry a signature artifact vs may be an acknowledgement.
 */

import { z } from 'zod'
import { SignerRoleSchema } from './signed-agreement'

export const RequirementSubjectGranularitySchema = z.enum(['per_subject', 'per_order'])
export type RequirementSubjectGranularity = z.infer<typeof RequirementSubjectGranularitySchema>

export const RequirementCompletionTimingSchema = z.enum(['at_registration', 'before_attendance'])
export type RequirementCompletionTiming = z.infer<typeof RequirementCompletionTimingSchema>

export const SignableRequirementHostTypeSchema = z.enum(['ticketed_event'])
export type SignableRequirementHostType = z.infer<typeof SignableRequirementHostTypeSchema>

/** Non-empty, de-duplicated set of signer roles that must each sign. */
export const RequiredSignerRolesSchema = z
  .array(SignerRoleSchema)
  .nonempty()
  .refine(roles => new Set(roles).size === roles.length, {
    message: 'requiredSignerRoles must not contain duplicates'
  })
export type RequiredSignerRoles = z.infer<typeof RequiredSignerRolesSchema>

export const SignableRequirementSchema = z.object({
  /** Requirement publicId. */
  id: z.string(),
  hostType: SignableRequirementHostTypeSchema,
  hostPublicId: z.string(),
  /** Form publicId (soft-FK into the forms system). */
  documentPublicId: z.string(),
  label: z.string(),
  required: z.boolean(),
  subjectGranularity: RequirementSubjectGranularitySchema,
  completionTiming: RequirementCompletionTimingSchema,
  requiredSignerRoles: RequiredSignerRolesSchema,
  requiresSignature: z.boolean(),
  sortOrder: z.number().int()
})
export type SignableRequirement = z.infer<typeof SignableRequirementSchema>

export const CreateSignableRequirementSchema = z.object({
  hostType: SignableRequirementHostTypeSchema,
  hostPublicId: z.string().min(1),
  documentPublicId: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean().optional(),
  subjectGranularity: RequirementSubjectGranularitySchema.optional(),
  completionTiming: RequirementCompletionTimingSchema.optional(),
  requiredSignerRoles: RequiredSignerRolesSchema,
  requiresSignature: z.boolean().optional(),
  sortOrder: z.number().int().optional()
})
export type CreateSignableRequirementInput = z.infer<typeof CreateSignableRequirementSchema>

// `hostPublicId` / `documentPublicId` are intentionally excluded from update:
// reassigning either bypasses the create-time cross-school ownership check.
export const UpdateSignableRequirementSchema = z.object({
  label: z.string().min(1).optional(),
  required: z.boolean().optional(),
  subjectGranularity: RequirementSubjectGranularitySchema.optional(),
  completionTiming: RequirementCompletionTimingSchema.optional(),
  requiredSignerRoles: RequiredSignerRolesSchema.optional(),
  requiresSignature: z.boolean().optional(),
  sortOrder: z.number().int().optional()
})
export type UpdateSignableRequirementInput = z.infer<typeof UpdateSignableRequirementSchema>
